import { DocumentEntity, DocumentItemTypeEnum } from '@domain';
import {
    PdfPreviewInteractions,
    PdfPreviewItemActions,
} from '../../../pdf-preview/pdf-preview-composer';

/**
 * Tipos que o renderer desenha na folha (o resto vira UNKNOWN e não aparece), menos a
 * quebra de página, que não desenha nada e portanto nunca tem menu.
 */
const MENU_TYPES: DocumentItemTypeEnum[] = [
    DocumentItemTypeEnum.TITLE,
    DocumentItemTypeEnum.SUBTITLE,
    DocumentItemTypeEnum.TEXT,
    DocumentItemTypeEnum.IMAGE,
    DocumentItemTypeEnum.GROUP_IMAGE,
    DocumentItemTypeEnum.VIDEO,
    DocumentItemTypeEnum.SIGNATURE,
    DocumentItemTypeEnum.CALLOUT,
    DocumentItemTypeEnum.STATS,
    DocumentItemTypeEnum.TABLE,
    DocumentItemTypeEnum.SEMAPHORE,
    DocumentItemTypeEnum.CHART,
];

/**
 * O que conta como conteúdo ao decidir se a SEÇÃO começa em folha nova: a quebra entra aqui
 * (ela é o próprio pedido de folha nova), a pasta de checklist não (não imprime nada).
 */
const RENDERED_TYPES: DocumentItemTypeEnum[] = [...MENU_TYPES, DocumentItemTypeEnum.PAGE_BREAK];

/** Tipos com painel de "configurações deste item": os que têm o botão de design no rail. */
export const SETTINGS_TYPES: DocumentItemTypeEnum[] = [
    DocumentItemTypeEnum.IMAGE,
    DocumentItemTypeEnum.GROUP_IMAGE,
    DocumentItemTypeEnum.VIDEO,
    DocumentItemTypeEnum.CALLOUT,
    DocumentItemTypeEnum.STATS,
    DocumentItemTypeEnum.TABLE,
    DocumentItemTypeEnum.SEMAPHORE,
];

/**
 * O que a prévia pode oferecer em cada item.
 *
 * A folha sabe onde o item CAIU na paginação (se abre a página ou não); só aqui se sabe o
 * que ele É. As regras da quebra são as mesmas do card:
 *
 * - dentro de pasta de checklist não se oferece quebra nenhuma;
 * - desfazer só aparece quando tirar o atributo traz mesmo o bloco de volta — no primeiro
 *   item de uma seção, com a quebra por seção ligada, a seção nasceria em folha nova de
 *   qualquer jeito, e um botão que não faz nada é pior que botão nenhum.
 *
 * Função pura para as regras ficarem testáveis sem montar o painel inteiro.
 */
export function buildPreviewInteractions(
    document: DocumentEntity,
    sectionPageBreak: boolean,
    labels: PdfPreviewInteractions['labels'],
): PdfPreviewInteractions {
    const items: Record<string, PdfPreviewItemActions> = {};

    for (const session of document?.sessions ?? []) {
        const sessionItems = session.items ?? [];
        const first = sessionItems.find(item => RENDERED_TYPES.includes(item.type));

        for (const item of sessionItems) {
            if (!MENU_TYPES.includes(item.type) || !item._id) continue;

            const insideFolder = (item.parentFolderId ?? null) !== null;
            const breaks = item.configs?.pageBreakBefore === true;

            items[item._id] = {
                canBreak: !insideFolder && !breaks,
                canUndoBreak: !insideFolder && breaks && !(item === first && sectionPageBreak),
                hasSettings: SETTINGS_TYPES.includes(item.type),
            };
        }
    }

    return { labels, items };
}
